// Validation and wording for consultation-form answers. It mirrors
// ValidateAnswers and HealthAnswers in api/internal/catalog/forms.go, rule for
// rule and message for message, so the form tells the client what the API
// would; the API still has the last word.

import type { ChecklistAnswer, FormAnswers, FormField, YesNoAnswer } from '../api/client'

export const MAX_ANSWER_LENGTH = 5000

// ANSWER_PREFIX starts the server's problem key for an answer: answers.<field id>.
export const ANSWER_PREFIX = 'answers.'

const REQUIRED = 'Please answer this.'
const NOT_VALID = "That answer isn’t valid."
const STATEMENT = 'Please tick to agree.'
const TOO_LONG = 'Keep this to 5,000 characters or fewer.'
const BAD_DATE = 'Enter a real date.'

// Headings and text blocks show; everything else takes an answer.
export function isAnswerable(f: FormField): boolean {
  return f.type !== 'heading' && f.type !== 'text'
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const runes = (s: string) => Array.from(s).length

// A real calendar date, YYYY-MM-DD.
function isRealDate(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return false
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const t = new Date(Date.UTC(y, mo - 1, d))
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
}

type Checked = { problem: string } | { present: false } | { present: true }

// checkAnswer is normaliseAnswer in the Go: a problem, "nothing given", or fine.
function checkAnswer(f: FormField, raw: unknown): Checked {
  const missing = { present: false } as const
  const fine = { present: true } as const
  switch (f.type) {
    case 'short':
    case 'long': {
      if (typeof raw !== 'string') return { problem: NOT_VALID }
      const s = raw.trim()
      if (runes(s) > MAX_ANSWER_LENGTH) return { problem: TOO_LONG }
      return s === '' ? missing : fine
    }
    case 'yes_no': {
      if (!isRecord(raw)) return { problem: NOT_VALID }
      const { value, details } = raw as { value?: unknown; details?: unknown }
      if ((value !== undefined && value !== null && typeof value !== 'boolean') || (details !== undefined && typeof details !== 'string')) {
        return { problem: NOT_VALID }
      }
      if (value === undefined || value === null) return missing
      if (runes((details ?? '').trim()) > MAX_ANSWER_LENGTH) return { problem: TOO_LONG }
      return fine
    }
    case 'checklist': {
      if (!isRecord(raw)) return { problem: NOT_VALID }
      const { ticked = [], none = false } = raw as { ticked?: unknown; none?: unknown }
      if (!Array.isArray(ticked) || typeof none !== 'boolean') return { problem: NOT_VALID }
      const known = new Set(f.options.map((o) => o.id))
      const set = new Set<string>()
      for (const id of ticked) {
        if (typeof id !== 'string' || !known.has(id)) return { problem: NOT_VALID }
        set.add(id)
      }
      if (none && (!f.none_option || set.size > 0)) return { problem: NOT_VALID }
      return set.size === 0 && !none ? missing : fine
    }
    case 'choice': {
      if (typeof raw !== 'string') return { problem: NOT_VALID }
      if (raw === '') return missing
      return f.options.some((o) => o.id === raw) ? fine : { problem: NOT_VALID }
    }
    case 'date': {
      if (typeof raw !== 'string') return { problem: NOT_VALID }
      const s = raw.trim()
      if (s === '') return missing
      return isRealDate(s) ? fine : { problem: BAD_DATE }
    }
    case 'statement': {
      if (typeof raw !== 'boolean') return { problem: NOT_VALID }
      return raw ? fine : missing
    }
  }
  return { problem: NOT_VALID }
}

// validateAnswers returns a message for each answer that is missing or not
// valid, keyed by field id. An empty result means the form can be sent.
export function validateAnswers(fields: FormField[], answers: Record<string, unknown>): Record<string, string> {
  const problems: Record<string, string> = {}
  for (const f of fields) {
    if (!isAnswerable(f)) continue
    const mustAnswer = f.required || f.type === 'statement'
    const requiredMessage = f.type === 'statement' ? STATEMENT : REQUIRED
    const raw = answers[f.id]
    if (raw === undefined || raw === null) {
      if (mustAnswer) problems[f.id] = requiredMessage
      continue
    }
    const r = checkAnswer(f, raw)
    if ('problem' in r) problems[f.id] = r.problem
    else if (!r.present && mustAnswer) problems[f.id] = requiredMessage
  }
  return problems
}

export interface HealthAnswer {
  fieldId: string
  label: string
  value: string
}

// healthAnswers lists the notable answers to health questions, in field
// order: a yes (with its details), the ticked conditions, or any other given
// answer. A no, "none" and unanswered questions are left out.
export function healthAnswers(fields: FormField[], answers: FormAnswers | Record<string, unknown>): HealthAnswer[] {
  const out: HealthAnswer[] = []
  for (const f of fields) {
    if (!f.health || !isAnswerable(f) || f.type === 'statement') continue
    const raw = answers[f.id]
    if (raw === undefined || raw === null) continue
    let value = ''
    switch (f.type) {
      case 'short':
      case 'long':
      case 'date':
        if (typeof raw === 'string') value = raw.trim()
        break
      case 'choice':
        value = f.options.find((o) => o.id === raw)?.label ?? ''
        break
      case 'yes_no': {
        const y = raw as YesNoAnswer
        if (isRecord(raw) && y.value === true) {
          value = 'Yes'
          const details = (y.details ?? '').trim()
          if (details) value += ': ' + details
        }
        break
      }
      case 'checklist': {
        const c = raw as ChecklistAnswer
        if (isRecord(raw) && Array.isArray(c.ticked)) {
          value = f.options.filter((o) => c.ticked.includes(o.id)).map((o) => o.label).join(', ')
        }
        break
      }
    }
    if (value) out.push({ fieldId: f.id, label: f.label, value })
  }
  return out
}
